import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/query';
import { addBreadcrumb } from '@sentry/react';
import { config } from '@/config';
import { apiLogger } from '@/core/utils/logger';
import { getEndpointTimeout } from './timeoutConfig';
import { AuthErrorClassifier } from '@voley/shared-utils/auth';
import type { RtkQueryError } from '@voley/shared-types/api';
import { withTimeoutAbortable } from '@voley/shared-utils/with-timeout';
import { getBootId, isEarlyBoot, getTimeSinceBoot, getBootAttempt } from '@/core/utils/bootSession';

// Extracted modules
import { isE2EMockAuthMode, getE2EMockResponse } from './e2eMocks';
import {
  logRequestInstrumentation,
  generateRequestId,
  logNetworkFailure,
  extractAbortReason,
} from './requestInstrumentation';
import {
  updateCsrfCache,
  clearCsrfCache,
  ensureCsrfToken,
  isCsrfError,
  attemptCsrfRecovery,
  attachCsrfHeader,
  type PrepareHeadersExtra,
} from './csrf';
import {
  isSessionInvalidated,
  handleAuthenticationLost,
  attemptSingleFlightRefresh,
  isAuthFlowEndpoint,
} from './sessionManager';
import {
  circuitBreaker,
  AUTH_FAILURE_WINDOW,
  MAX_NETWORK_FAILURES,
  NETWORK_FAILURE_WINDOW,
  recordAuthFailure,
  recordNetworkFailure,
  resetAuthFailures,
  resetNetworkFailures,
} from './circuitBreaker';

// Re-export public API for existing consumers
export { clearCsrfCache } from './csrf';
export { markSessionInvalidated, isSessionInvalidated, resetSessionInvalidated } from './sessionManager';

/** Only GET/DELETE/HEAD/OPTIONS are safe to retry without side effects */
function isIdempotentRequest(args: string | FetchArgs): boolean {
  if (typeof args === 'string') return true;
  const method = (args.method || 'GET').toUpperCase();
  return ['GET', 'DELETE', 'HEAD', 'OPTIONS'].includes(method);
}

const FORM_DATA_ENDPOINTS = new Set([
  'sendMessage', 'uploadProfileImage', 'uploadChannelAvatar',
  'createContactsImportJob', 'createNumberRegistration',
]);

const baseQueryConfig: Parameters<typeof fetchBaseQuery>[0] = {
  baseUrl: config.api.baseUrl,
  credentials: 'include',
  prepareHeaders: async (headers: Headers, { endpoint, extra }: { endpoint: string; extra: unknown }) => {
    headers.set('x-request-timeout-ms', getEndpointTimeout(endpoint as string).toString());
    headers.set('x-request-id', (extra as PrepareHeadersExtra)?.requestId || generateRequestId());
    headers.set('x-boot-id', getBootId());
    headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
    headers.set('Pragma', 'no-cache');
    if (typeof window !== 'undefined') {
      await attachCsrfHeader(headers, endpoint as string, extra as PrepareHeadersExtra);
    }
    const isBodyless = String(endpoint || '').includes('resendInvitation') || String(endpoint || '').includes('deleteOrganizationMember');
    if (!headers.get('Content-Type') && !FORM_DATA_ENDPOINTS.has(endpoint as string) && !isBodyless) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  }
};

/** Base query with timeout support using withTimeoutAbortable */
const baseFetchQueryWithTimeout = fetchBaseQuery({
  ...baseQueryConfig,
  fetchFn: async (input: RequestInfo | URL, init?: RequestInit) => {
    let timeoutMs = 15000;
    const extractTimeoutHeader = (headers?: HeadersInit | null): string | undefined => {
      if (!headers) return undefined;
      if (headers instanceof Headers) return headers.get('x-request-timeout-ms') || undefined;
      if (Array.isArray(headers)) return headers.find(([key]) => key.toLowerCase() === 'x-request-timeout-ms')?.[1];
      return (headers as Record<string, string | undefined>)['x-request-timeout-ms'];
    };
    if (input instanceof Request) {
      const h = input.headers.get('x-request-timeout-ms');
      if (h) timeoutMs = Number(h);
    } else {
      const h = extractTimeoutHeader(init?.headers || null);
      if (h) timeoutMs = Number(h);
    }
    return withTimeoutAbortable({ fn: (signal: AbortSignal) => fetch(input, {...init, signal }), ms: timeoutMs });
  },
});


export const baseQueryWithRetry: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // If session is permanently invalidated, short-circuit immediately
  if (isSessionInvalidated()) {
    return {
      error: {
        status: 401,
        data: { message: 'Session expired' },
      } as FetchBaseQueryError,
    };
  }

  // Generate request ID for correlation with server logs
  const requestId = generateRequestId();
  const startTime = performance.now();
  let retryCount = 0;

  // Extract request details for logging
  const url = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET').toUpperCase();

  // E2E Mock Interception: Return mock data for critical endpoints during E2E testing
  if (url) {
    const isE2E = isE2EMockAuthMode();
    if (isE2E) {
      apiLogger.debug('[E2E] Mock mode active, checking URL:', url);
    }
    const mockResponse = getE2EMockResponse(url, method);
    if (mockResponse) {
      apiLogger.debug('[E2E] Returning mock response for:', url);
      return mockResponse as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    }
  }

  // Determine if request is GET to skip CSRF token
  const isGet = typeof args === 'string' || !args.method || args.method.toUpperCase() === 'GET';
  extraOptions = { ...(extraOptions as object || {}), isGet, requestId };

  // For mutations, attach an idempotency key header so the backend can safely
  // deduplicate retried requests (e.g. when a gateway 502 causes FETCH_ERROR
  // and we retry). Reuses requestId so all retry attempts carry the same key.
  if (!isGet && typeof args !== 'string') {
    const prevHeaders = args.headers;
    args = {
      ...args,
      headers: {
        ...(prevHeaders && typeof prevHeaders === 'object'
            && !Array.isArray(prevHeaders)
            && !(prevHeaders instanceof Headers)
          ? (prevHeaders as Record<string, string>)
          : {}),
        'x-idempotency-key': requestId,
      },
    };
  }

  // Reject requests with obviously invalid IDs before sending
  if (url?.toLowerCase().match(/\/(organizations|users|contacts|conversations)\/($|\?|undefined|null)/)) {
    const errorMsg = `Invalid request: URL contains empty or undefined ID segment: ${url}`;
    apiLogger.error(`[baseQueryWithRetry] ${errorMsg}`, { endpoint: api.endpoint, url, bootId: getBootId(), timeSinceBoot: getTimeSinceBoot() });
    addBreadcrumb({ category: 'invalid-request', message: errorMsg, level: 'error',
      data: { endpoint: api.endpoint, url, bootId: getBootId(), timeSinceBoot: getTimeSinceBoot(), isEarlyBoot: isEarlyBoot() } });
    return { error: { status: 'CUSTOM_ERROR', error: errorMsg, data: { message: 'Request blocked: missing required ID parameter' } } } as QueryReturnValue<unknown, FetchBaseQueryError, object>;
  }

  const logFailure = (r: { error: FetchBaseQueryError }, count: number, reason?: string) => {
    const errorObj = r.error as FetchBaseQueryError & { error?: string };
    logNetworkFailure({
      requestId, endpoint: api.endpoint, url: url || 'unknown', method,
      status: r.error.status, durationMs: Math.round(performance.now() - startTime),
      abortReason: reason || extractAbortReason(errorObj), retryCount: count,
      errorMessage: typeof errorObj.data === 'object' && errorObj.data !== null
        ? (errorObj.data as Record<string, unknown>).message as string || errorObj.error : errorObj.error,
      timestamp: new Date().toISOString(),
    });
  };

  // Make the initial request
  let result = await baseFetchQueryWithTimeout(args, api, extraOptions);

  const durationMs = Math.round(performance.now() - startTime);
  logRequestInstrumentation({
    endpoint: api.endpoint, fullUrl: `${config.api.baseUrl}/${url || ''}`, method,
    headerKeys: ['x-request-id', 'x-boot-id', 'x-csrf-token', 'x-request-timeout-ms', 'Content-Type', 'Cache-Control'],
    credentialsMode: 'include', durationMs, retryCount, requestId,
    bootId: getBootId(), timeSinceBoot: getTimeSinceBoot(), isEarlyBoot: isEarlyBoot(),
    status: result.error?.status,
    abortReason: result.error ? extractAbortReason(result.error as FetchBaseQueryError & { error?: string }) : undefined,
    error: result.error ? (typeof result.error.data === 'object' && result.error.data
      ? (result.error.data as Record<string, unknown>).message as string : String(result.error.status)) : undefined,
  });

  // Update CSRF token from successful responses
  if (result.data && typeof result.data === 'object') {
    const rd = result.data as Record<string, unknown>;
    const ft = rd.csrfToken || rd.csrf_token || rd.token;
    if (ft && typeof ft === 'string') updateCsrfCache(ft);
  }

  const endpoint = typeof args === 'string' ? args : args.url;
  if (result.error) apiLogger.info(`[baseQueryWithRetry] Request to ${endpoint} failed with status:`, result.error.status);

  // Timeout retry for idempotent requests
  if (result.error && isIdempotentRequest(args)) {
    const eObj = result.error as { status: string; error?: string };
    const eStr = String(eObj.error || '');
    const isTimeout = eObj.status === 'TIMEOUT_ERROR' ||
      (eObj.status === 'FETCH_ERROR' && (eStr.includes('TimeoutError') || eStr.includes('timed out')));
    if (isTimeout) {
      const cfgTimeout = getEndpointTimeout(api.endpoint);
      const tDur = Math.round(performance.now() - startTime);
      const bc = {
        endpoint: api.endpoint, urlPath: url?.replace(/\?.*/, '') || 'unknown', method, requestId,
        configuredTimeoutMs: cfgTimeout, actualDurationMs: tDur, exceededBy: tDur - cfgTimeout,
        bootId: getBootId(), bootAttempt: getBootAttempt(), timeSinceBoot: getTimeSinceBoot(),
        isEarlyBoot: isEarlyBoot(), browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
        retryCount, abortReason: 'timeout' as const,
      };
      if (process.env.NODE_ENV === 'development') {
        apiLogger.error('[TIMEOUT_ERROR]', bc);
      }
      addBreadcrumb({ category: 'timeout', level: 'warning', data: bc,
        message: `TIMEOUT: ${method} ${api.endpoint} after ${tDur}ms (limit: ${cfgTimeout}ms)` });
      apiLogger.warn(`[baseQueryWithRetry] TIMEOUT on ${method} ${endpoint}`, bc);
      for (let i = 1; i <= 2; i++) {
        retryCount++;
        const bd = 500 * Math.pow(2, i - 1);
        const delay = Math.round(bd + bd * 0.3 * Math.random());
        apiLogger.info(`[baseQueryWithRetry] Timeout retry ${i}/2 after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
        if (!rr.error) return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>;
        const re = rr.error as { status: string; error?: string };
        const rs = String(re.error || '');
        const still = re.status === 'TIMEOUT_ERROR' || (re.status === 'FETCH_ERROR' && (rs.includes('TimeoutError') || rs.includes('timed out')));
        if (!still) { logFailure(rr as { error: FetchBaseQueryError }, retryCount, 'timeout_retry_different_error'); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
        if (i === 2) { logFailure(rr as { error: FetchBaseQueryError }, retryCount, 'timeout'); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
      }
    }
  }

  // Mutation timeout retry — single retry with idempotency key
  // SKIP for FormData bodies: FormData streams are consumed on first fetch and cannot be re-sent.
  // Component-level retry (useImageUpload / ImageUploadButton) creates fresh FormData per attempt.
  const isFormDataBody = typeof args !== 'string' && args.body instanceof FormData;
  if (result.error && !isIdempotentRequest(args) && retryCount === 0 && !isFormDataBody) {
    const me = result.error as { status: string; error?: string };
    const ms = String(me.error || '');
    const isMutTimeout = me.status === 'TIMEOUT_ERROR' || (me.status === 'FETCH_ERROR' && (ms.includes('TimeoutError') || ms.includes('timed out')));
    if (isMutTimeout) {
      const d = 500 + Math.round(500 * Math.random());
      apiLogger.warn(`[baseQueryWithRetry] Timeout on mutation ${method} ${endpoint}, retrying once after ${d}ms (key: ${requestId.substring(0, 8)}...)`);
      retryCount++;
      await new Promise(resolve => setTimeout(resolve, d));
      const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
      if (!rr.error) { apiLogger.info(`[baseQueryWithRetry] Mutation timeout retry succeeded for ${endpoint}`); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
      logFailure(rr as { error: FetchBaseQueryError }, retryCount, 'mutation_timeout_retry');
      return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    }
  }

  // 401 RECOVERY - Single-flight refresh
  if (result.error && result.error.status === 401 && !isAuthFlowEndpoint(typeof args === 'string' ? args : args.url)) {
    apiLogger.info('[baseQueryWithRetry] 401, attempting refresh', { endpoint: api.endpoint, bootId: getBootId() });
    const refreshResult = await attemptSingleFlightRefresh();
    if (refreshResult.success) {
      retryCount++;
      const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
      if (!rr.error) { resetAuthFailures(); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
      logFailure(rr as { error: FetchBaseQueryError }, retryCount, 'retry_after_refresh_failed');
      if (rr.error?.status === 401) { handleAuthenticationLost(); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
      result = rr;
    } else if (refreshResult.transient) {
      // Transient failure (5xx, network error) — don't kill session, let user retry
      apiLogger.warn('[baseQueryWithRetry] Token refresh failed transiently, session preserved');
      return result as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    } else {
      // Permanent failure (401/403 from refresh endpoint) — session is truly expired
      handleAuthenticationLost();
      return result as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    }
  }

  // CSRF error recovery (403)
  if (result.error && isCsrfError(result.error)) {
    const rr = await attemptCsrfRecovery(baseFetchQueryWithTimeout, args, api, extraOptions, endpoint, result.error);
    if (rr) return rr;
  }

  // Non-CSRF auth-related 403s
  if (result.error && result.error.status === 403 && !isCsrfError(result.error)) {
    const ed = result.error.data as Record<string, unknown> | undefined;
    const em = String(ed?.message || ed?.error || '').toLowerCase();
    const authRelated = em.includes('unauthorized') || em.includes('session') || em.includes('authentication');
    if (authRelated && !isAuthFlowEndpoint(typeof args === 'string' ? args : args.url)) {
      const refreshed = await attemptSingleFlightRefresh();
      if (refreshed) { const rr = await baseFetchQueryWithTimeout(args, api, extraOptions); if (!rr.error) return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
    }
  }

  // Upload 400: file not received / stream interrupted → retry with fresh CSRF
  // Only retry non-sendMessage FormData uploads (e.g. profile image, channel avatar).
  // sendMessage FormData retries are handled by onQueryStarted which creates fresh FormData.
  const isFormDataUpload = typeof args !== 'string' && args.body instanceof FormData;
  if (result.error && result.error.status === 400 && !isFormDataBody && (endpoint?.includes('upload') || endpoint?.includes('profile-image') || isFormDataUpload)) {
    const ed = result.error.data as Record<string, unknown> | undefined;
    apiLogger.error('[baseQueryWithRetry] Upload 400', { endpoint, errorMessage: ed?.message || ed?.error || 'No error message' });
    const em = String(ed?.message || ed?.error || '').toLowerCase();
    if (em.includes('no image') || em.includes('no file') || em.includes('interrupted') || em.includes('cancelled') || em.includes('incomplete')) {
      clearCsrfCache(); retryCount++;
      try {
        const ft = await ensureCsrfToken();
        if (ft) { await new Promise(r => setTimeout(r, 200)); const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
          if (!rr.error) return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>;
          logFailure(rr as { error: FetchBaseQueryError }, retryCount, 'upload_file_not_received'); }
      } catch (e) { apiLogger.error('[baseQueryWithRetry] Upload retry error', e); }
    }
  }

  // Login transient retry
  try {
    const lp = typeof args === 'string' ? args : args.url;
    if (lp?.includes('/auth/login') && result.error) {
      const st = (result.error as FetchBaseQueryError)?.status;
      if (st === 'FETCH_ERROR' || st === 'CUSTOM_ERROR' || st === 0) {
        retryCount++; await new Promise(r => setTimeout(r, 300));
        result = await baseFetchQueryWithTimeout(args, api, extraOptions);
        if (result.error) logFailure(result as { error: FetchBaseQueryError }, retryCount, 'login_transient');
      }
    }
  } catch (e) { apiLogger.debug('[baseQueryWithRetry] Login retry check failed:', e); }

  // Circuit breaker with graduated error handling
  if (result.error) {
    const now = Date.now();
    const status = result.error.status;
    // FetchBaseQueryError has data: unknown; cast to RtkQueryError for typed classification
    const errorClass = AuthErrorClassifier.classify(result.error as unknown as RtkQueryError);
    if (status === 'FETCH_ERROR' || status === 0) {
      const es = String((result.error as FetchBaseQueryError & { error?: string }).error || '');
      const isTO = es.includes('TimeoutError') || es.includes('timed out');
      if (!isTO) {
        // Immediate retry for network errors on idempotent requests
        if (retryCount === 0 && isIdempotentRequest(args)) {
          const d = 100 + Math.round(100 * Math.random());
          const ar = extractAbortReason(result.error as FetchBaseQueryError & { error?: string });
          apiLogger.info(`[baseQueryWithRetry] Network error on ${method} ${endpoint}, reason=${ar || 'unknown'}, retry after ${d}ms`);
          retryCount++; await new Promise(r => setTimeout(r, d));
          const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
          if (!rr.error) return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>;
          result = rr;
        }
        // Single retry for FETCH_ERROR on mutations (skip FormData — stream already consumed)
        if (retryCount === 0 && !isIdempotentRequest(args) && !isFormDataBody) {
          const d = 500 + Math.round(500 * Math.random());
          const ar = extractAbortReason(result.error as FetchBaseQueryError & { error?: string });
          apiLogger.warn(`[baseQueryWithRetry] FETCH_ERROR mutation ${method} ${endpoint}, reason=${ar || 'unknown'}, retry after ${d}ms (key: ${requestId.substring(0, 8)}...)`);
          retryCount++; await new Promise(r => setTimeout(r, d));
          const rr = await baseFetchQueryWithTimeout(args, api, extraOptions);
          if (!rr.error) { resetNetworkFailures(); return rr as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
          result = rr; logFailure(result as { error: FetchBaseQueryError }, retryCount, 'mutation_fetch_error_retry');
        }
        // Circuit breaker for repeated network failures
        const nf = recordNetworkFailure(now);
        if (nf >= MAX_NETWORK_FAILURES) {
          if (errorClass.retryable && errorClass.suggestedAction === 'retry' && isIdempotentRequest(args)) {
            const d = AuthErrorClassifier.getRetryDelay(result.error as unknown as RtkQueryError, circuitBreaker.networkFailures);
            retryCount++; await new Promise(r => setTimeout(r, d));
            result = await baseFetchQueryWithTimeout(args, api, extraOptions);
            if (!result.error) { resetNetworkFailures(); return result as QueryReturnValue<unknown, FetchBaseQueryError, object>; }
            logFailure(result as { error: FetchBaseQueryError }, retryCount, 'circuit_breaker_network');
          } else if (errorClass.retryable && !isIdempotentRequest(args)) {
            logFailure(result as { error: FetchBaseQueryError }, retryCount, 'non_idempotent_skipped');
          }
        }
      }
    }
    if (status === 401 && !endpoint?.includes('login')) {
      recordAuthFailure(now);
    }
  } else {
    const now = Date.now();
    if (now - circuitBreaker.lastAuthFailure > AUTH_FAILURE_WINDOW) circuitBreaker.authFailures = 0;
    if (now - circuitBreaker.lastNetworkFailure > NETWORK_FAILURE_WINDOW) circuitBreaker.networkFailures = 0;
  }

  // 429 Rate Limit
  if (result.error && result.error.status === 429) {
    if (isIdempotentRequest(args)) {
      const rah = (result.meta as { response?: Response })?.response?.headers?.get('retry-after');
      let rd = 60000;
      if (rah) { const s = parseInt(rah, 10); if (!isNaN(s)) rd = Math.min(s * 1000, 120000); }
      apiLogger.warn(`[baseQueryWithRetry] 429 Rate Limited, waiting ${rd}ms`);
      retryCount++; await new Promise(r => setTimeout(r, rd));
      result = await baseFetchQueryWithTimeout(args, api, extraOptions);
      if (!result.error) return result as QueryReturnValue<unknown, FetchBaseQueryError, object>;
      logFailure(result as { error: FetchBaseQueryError }, retryCount, 'rate_limited');
    } else {
      logFailure(result as { error: FetchBaseQueryError }, retryCount, 'rate_limited_non_idempotent');
    }
    if (result.error?.status === 429) {
      return { error: { ...result.error, data: { ...(typeof result.error.data === 'object' && result.error.data !== null ? result.error.data : {}), message: 'Too many requests. Please wait a moment and try again.' } } } as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    }
  }

  // 5xx Server Error retry (idempotent only) — handles 502, 503, 504
  const serverErrorCodes: readonly number[] = [502, 503, 504];
  if (result.error && typeof result.error.status === 'number' && serverErrorCodes.includes(result.error.status)) {
    const sc = result.error.status as number;
    if (isIdempotentRequest(args)) {
      const baseD = sc === 503 ? 200 : 500;
      const maxD = sc === 503 ? 1000 : 2000;
      for (let i = 1; i <= 2; i++) {
        retryCount++;
        const d = Math.min(baseD * Math.pow(2, i - 1), maxD);
        await new Promise(r => setTimeout(r, d));
        result = await baseFetchQueryWithTimeout(args, api, extraOptions);
        if (!result.error || typeof result.error.status !== 'number' || !serverErrorCodes.includes(result.error.status)) { resetNetworkFailures(); break; }
      }
    }
    if (result.error && typeof result.error.status === 'number' && serverErrorCodes.includes(result.error.status)) {
      const reason = result.error.status === 502 ? 'bad_gateway' : result.error.status === 503 ? 'service_unavailable' : 'gateway_timeout';
      logFailure(result as { error: FetchBaseQueryError }, retryCount, reason);
      return { error: { ...result.error, data: { ...(typeof result.error.data === 'object' && result.error.data !== null ? result.error.data : {}), message: 'Server is temporarily unreachable. Please try again.' } } } as QueryReturnValue<unknown, FetchBaseQueryError, object>;
    }
  }

  if (result.error && result.error.status === 401 && !(typeof args === 'string' ? args : args.url)?.includes('/auth/login')) {
    apiLogger.info('[baseQueryWithRetry] Got 401, auth components will handle redirect');
  }
  if (result.error && retryCount === 0) logFailure(result as { error: FetchBaseQueryError }, retryCount);
  return result as QueryReturnValue<unknown, FetchBaseQueryError, object>;
};
